import { Route } from "nextjs-routes";
import { UserContextState } from "@src/lib/contexts/UserContext";
import {
  CoverImage,
  LibraryItemType,
  TrainingResourceVersionType,
  SharingPermission,
  UserType,
  PublishState,
  TrainingResourceType,
} from "@src/types.generated";
import { gql } from "@apollo/client";
import { CoverImageFragment } from "@src/fragments.generated";
import {
  HasLibraryItemEditPermission_LibraryItemFragment,
  LibraryItemOwner_LibraryItemFragment,
  ModuleOrPremiumContentLibraryItemTitle_LibraryItemFragment,
  PremiumLibraryItemUrl_LibraryItemFragment,
  TrainingResourceAvatar_LibraryItemFragment,
} from "@src/features/contentLibrary/utils.generated";
import ContentAvatar, {
  ContentType,
  getLibraryItemTypeCopy,
  LIBRARY_ITEM_TYPE_TO_CONTENT_TYPE,
} from "@src/features/contentLibrary/ContentAvatar";
import { getLinkType, linkTypeCopy, LinkType } from "@src/lib/utils/files";
import { RESOURCE_TYPE_FILTER_LABELS } from "@src/features/library/resources/resourceTypeFilter";
import {
  LibraryContentSharingPermissionFragment,
  LibraryUserSharingPermissionFragment,
} from "@src/features/contentLibrary/utils.generated";
import { ModuleLibraryPaginatedTable_LibraryItemFragment } from "../library/modules/ModulesLibraryPaginatedTable.generated";
import {
  getOrgPremiumContentConfigFragmentStatus,
  getOrgSharpConfigStatus,
} from "@src/features/sexualHarassmentPrevention/utils/premiumModules";
import { ReactNode, useCallback } from "react";
import useUser from "@src/features/app/hooks/useUser";
import { ModuleOverflowMenuItems } from "./ModuleOverflowMenu";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import { PUBLISH_STATE_COPY } from "@src/features/contentDetail/utils/courses";
import { OrgPremiumContentConfigStatusFragment } from "@src/features/sexualHarassmentPrevention/utils/premiumModules.generated";
import { IsSharpSetupCompleteFragment } from "../sexualHarassmentPrevention/utils.generated";
import {
  Badge,
  STATUS_BADGE_CONFIG,
  StatusBadgeVariant,
} from "@src/lib/ui/Badge";

export const getFileDetailRoute = (libraryItemId: string): Route => {
  return {
    pathname: "/library/library-item/[id]",
    query: {
      id: libraryItemId,
    },
  };
};

type ResourceTypeLabelTrainingResource = {
  trainingResource?: {
    draftVersion?: {
      type?: TrainingResourceVersionType | null;
      media?: { mediaUrls?: { en?: string | null } | null } | null;
    } | null;
    publishedVersion?: {
      type?: TrainingResourceVersionType | null;
      media?: { mediaUrls?: { en?: string | null } | null } | null;
    } | null;
  } | null;
};

export const getResourceTypeLabel = (
  item: ResourceTypeLabelTrainingResource,
): string | null => {
  const version =
    item.trainingResource?.draftVersion ??
    item.trainingResource?.publishedVersion;
  if (!version) return null;
  if (version.type === TrainingResourceVersionType.RichTextDocument) {
    return "Opus Doc";
  }
  const mediaUrl = version.media?.mediaUrls?.en;
  if (!mediaUrl) return null;
  const linkType = getLinkType(mediaUrl);
  if (linkType === LinkType.LINK) return null;
  return linkTypeCopy[linkType];
};

export const getLibraryItemTypeLabel = (
  item: ResourceTypeLabelTrainingResource & {
    type: LibraryItemType;
    resourceType?: TrainingResourceType | null;
  },
): string => {
  const libraryItemTypeCopy = getLibraryItemTypeCopy();
  if (item.type !== LibraryItemType.TrainingResource) {
    return libraryItemTypeCopy[item.type];
  }
  if (item.resourceType) {
    return RESOURCE_TYPE_FILTER_LABELS[item.resourceType];
  }
  return getResourceTypeLabel(item) ?? libraryItemTypeCopy[item.type];
};

export const renderTrainingResourceAvatar = (
  x: TrainingResourceAvatar_LibraryItemFragment,
  avatarProps?: Partial<React.ComponentProps<typeof ContentAvatar>>,
): ReactNode | undefined => {
  let coverImage: CoverImage | undefined = undefined;
  if (
    x.trainingResource?.draftVersion?.type === TrainingResourceVersionType.Media
  ) {
    coverImage = {
      id: x.trainingResource.id,
      imageUrls: x.trainingResource.draftVersion?.media?.imageUrls,
    };
  } else if (
    x.trainingResource?.draftVersion?.type ===
    TrainingResourceVersionType.RichTextDocument
  ) {
    coverImage = {
      id: x.trainingResource?.id,
      emojiIcon: "📄",
    };
  }
  return (
    <ContentAvatar
      size={"32px"}
      contentType={"Resource"}
      style={{ borderRadius: 8 }}
      media={x.trainingResource?.draftVersion?.media}
      coverImage={coverImage}
      defaultBackgroundHashKey={x.id}
      iconSize={16}
      {...avatarProps}
    />
  );
};

export const useRenderModuleOverflowMenu = (callbacks: {
  onRename: (item: ModuleLibraryPaginatedTable_LibraryItemFragment) => void;
  onArchive: (item: ModuleLibraryPaginatedTable_LibraryItemFragment) => void;
  onDuplicate: (item: ModuleLibraryPaginatedTable_LibraryItemFragment) => void;
  onUpdateAccess: (
    item: ModuleLibraryPaginatedTable_LibraryItemFragment,
  ) => void;
}): ((
  x: ModuleLibraryPaginatedTable_LibraryItemFragment,
) => ReactNode | undefined) => {
  const userContextState = useUser();
  return useCallback(
    (x: ModuleLibraryPaginatedTable_LibraryItemFragment) => {
      const itemSharingPermissions = x.sharingPermissions;
      const itemOwnerId = getModuleOwnerId(x);
      if (x.orgSharpConfig || x.orgPremiumContentConfig) {
        return null;
      } else if (
        userContextState?.user?.userType === UserType.Admin ||
        (!!itemSharingPermissions &&
          userHasEditPermissions(
            itemSharingPermissions,
            userContextState,
            itemOwnerId,
          ))
      ) {
        return (
          <ModuleOverflowMenuItems
            libraryItem={x}
            onRename={() => callbacks.onRename(x)}
            onArchive={() => callbacks.onArchive(x)}
            onDuplicate={() => callbacks.onDuplicate(x)}
            onUpdateAccess={() => callbacks.onUpdateAccess(x)}
          />
        );
      } else {
        return null;
      }
    },
    [userContextState, callbacks],
  );
};

export const renderModuleAvatar = (
  x: ModuleLibraryPaginatedTable_LibraryItemFragment,
): ReactNode | undefined => {
  let id: number | string = -1;
  let coverImage: CoverImage | undefined = {} as CoverImage;
  if (x.orgSharpConfig) {
    id = x.orgSharpConfig.id;
    coverImage = { id } as CoverImage;
  } else if (x.orgPremiumContentConfig) {
    id = x.orgPremiumContentConfig.id;
    coverImage =
      x.orgPremiumContentConfig.premiumContentConfig.catalogModule?.module
        .libraryItem.coverImage || undefined;
  } else {
    id = x.id;
    coverImage = x.coverImage || undefined;
  }
  return (
    <ContentAvatar
      size={"32px"}
      contentType={getLibraryItemContentType(x.type)}
      style={{ borderRadius: 8 }}
      coverImage={coverImage}
      defaultBackgroundHashKey={id}
      iconSize={16}
    />
  );
};

export const renderModulePublishedStatus = (
  x: ModuleLibraryPaginatedTable_LibraryItemFragment,
): string | null => {
  let publishedStatus = null;
  if (x.module) {
    return x.publishState ? PUBLISH_STATE_COPY[x.publishState] : null;
  }
  if (x.orgPremiumContentConfig) {
    publishedStatus = getOrgPremiumContentConfigFragmentStatus(
      x.orgPremiumContentConfig,
    );
  } else if (x.orgSharpConfig) {
    return getOrgSharpConfigStatus(x.orgSharpConfig);
  }
  return publishedStatus;
};

export type LibraryItemPublishedStatusInput = {
  publishState?: PublishState | null;
  orgPremiumContentConfig?: OrgPremiumContentConfigStatusFragment | null;
  orgSharpConfig?: IsSharpSetupCompleteFragment | null;
};

export const renderLibraryItemPublishedStatus = (
  x: LibraryItemPublishedStatusInput,
): string | null => {
  // Check publishState first for all types (Modules, Courses, Skills, Tasks, etc.)
  if (x.publishState) {
    return PUBLISH_STATE_COPY[x.publishState];
  }

  // Fall back to special configs for premium content
  if (x.orgPremiumContentConfig) {
    return getOrgPremiumContentConfigFragmentStatus(x.orgPremiumContentConfig);
  }

  if (x.orgSharpConfig) {
    return getOrgSharpConfigStatus(x.orgSharpConfig);
  }

  return null;
};

const PREMIUM_STATUS_TO_VARIANT: Record<string, StatusBadgeVariant> = {
  Active: "published",
  "Needs setup": "draft",
};

export function libraryItemPublishedStatusPill(
  x: LibraryItemPublishedStatusInput,
): ReactNode {
  const label = renderLibraryItemPublishedStatus(x);
  if (!label) return null;
  const variant =
    (x.publishState
      ? STATUS_BADGE_CONFIG[x.publishState]?.variant
      : PREMIUM_STATUS_TO_VARIANT[label]) ?? "archived";
  return <Badge variant={variant}>{label}</Badge>;
}

export const getModuleLibraryItemEditedAt = (
  x: ModuleLibraryPaginatedTable_LibraryItemFragment,
): string | undefined => {
  if (x.orgSharpConfig) {
    return x.orgSharpConfig.lastEditedAt || x.orgSharpConfig.createdAt;
  } else if (x.orgPremiumContentConfig) {
    return (
      x.orgPremiumContentConfig.lastEditedAt ??
      x.orgPremiumContentConfig.createdAt
    );
  } else {
    return x.lastEditedAt || x.createdAt;
  }
};

export const getPremiumLibraryItemUrl = (
  item: PremiumLibraryItemUrl_LibraryItemFragment,
  /** SHARP's page requires managePremiumContent; without it, no link (404). */
  canManagePremiumContent: boolean,
): Route | undefined => {
  if (
    item.orgPremiumContentConfig &&
    item.orgPremiumContentConfig.premiumContentConfig.catalogModule?.module.id
  ) {
    return getPremiumModuleRoute(
      item.orgPremiumContentConfig.premiumContentConfig.catalogModule
        .id as string,
    );
  } else if (item.orgSharpConfig && canManagePremiumContent) {
    return SHARP_DETAIL_ROUTE;
  }
};

gql`
  fragment PremiumLibraryItemUrl_LibraryItem on LibraryItem {
    id
    orgPremiumContentConfig {
      id
      premiumContentConfig {
        id
        catalogModule {
          id
          module {
            id
          }
        }
      }
    }
    orgSharpConfig {
      id
    }
  }
`;

export const SHARP_DETAIL_ROUTE: Route = {
  pathname: "/sexual-harassment-prevention",
};

export const getPremiumModuleRoute = (catalogModuleId: string): Route => {
  return {
    pathname: "/premium-module/[id]",
    query: {
      id: catalogModuleId.toString(),
    },
  };
};

export const getLibraryItemDetailRoute = (libraryItemId: string): Route => {
  return {
    pathname: "/library/library-item/[id]",
    query: {
      id: libraryItemId,
    },
  };
};

export const userHasEditPermissions = (
  sharingPermissions: LibraryContentSharingPermissionFragment,
  userContext: UserContextState,
  contentOwnerId?: number | undefined,
): boolean => {
  if (userContext.isStaff) {
    return true;
  }
  const userPermission = sharingPermissions.individuals.find(
    (i: LibraryUserSharingPermissionFragment) =>
      i.user.id === userContext.user?.id,
  );
  if (contentOwnerId && contentOwnerId === userContext.user?.id) {
    return true;
  }
  if (userPermission?.permission === SharingPermission.Edit) {
    return true;
  }
  if (
    userContext.user?.userType == UserType.Admin &&
    sharingPermissions.allAdmins == SharingPermission.Edit
  ) {
    return true;
  }
  if (
    userContext.user?.userType == UserType.Manager &&
    sharingPermissions.allManagers == SharingPermission.Edit
  ) {
    return true;
  }
  return false;
};

export const getModuleOrPremiumContentLibraryItemTitle = (
  x: ModuleOrPremiumContentLibraryItemTitle_LibraryItemFragment,
): string => {
  if (x.type === LibraryItemType.Module) {
    return x.name.en || "Untitled Module";
  } else if (x.orgSharpConfig) {
    return "Sexual Harassment Prevention Training";
  } else if (x.orgPremiumContentConfig) {
    return (
      x.orgPremiumContentConfig.premiumContentConfig.catalogModule?.module
        .libraryItem.name.en || "Untitled Module"
    );
  }
  return "";
};

gql`
  fragment ModuleOrPremiumContentLibraryItemTitle_LibraryItem on LibraryItem {
    id
    name {
      en
    }
    type
    orgSharpConfig {
      id
    }
    orgPremiumContentConfig {
      id
      premiumContentConfig {
        id
        catalogModule {
          id
          module {
            id
            libraryItem {
              id
              name {
                en
              }
            }
          }
        }
      }
    }
  }
`;

type ShouldDisableMultiSelectForItem = (
  sharingPermissions: LibraryContentSharingPermissionFragment,
  ownerUserId: number | undefined,
) => boolean;

export const useShouldDisableMultiSelectForItem =
  (): ShouldDisableMultiSelectForItem => {
    const userContextState = useUser();
    const { userActionPermissions } = useActionPermissions();
    return useCallback(
      (
        sharingPermissions: LibraryContentSharingPermissionFragment,
        ownerUserId: number | undefined,
      ) => {
        if (userActionPermissions?.manageLibrary === false) {
          return true;
        }
        return (
          !!sharingPermissions &&
          !userHasEditPermissions(
            sharingPermissions,
            userContextState,
            ownerUserId,
          )
        );
      },
      [userActionPermissions?.manageLibrary, userContextState],
    );
  };

export const getLibraryItemContentType = (x: LibraryItemType): ContentType => {
  return LIBRARY_ITEM_TYPE_TO_CONTENT_TYPE[x];
};

export const getLibraryItemOwnerName = (
  x: LibraryItemOwner_LibraryItemFragment,
): string | undefined => {
  let owner: string | undefined = undefined;
  if (x.orgPremiumContentConfig) {
    owner = x.orgPremiumContentConfig.createdBy?.name;
  } else if (x.orgSharpConfig) {
    owner = x.orgSharpConfig.createdBy?.name;
  } else {
    owner = x.createdBy?.name;
  }
  return owner;
};

export const getModuleOwnerId = (
  x: LibraryItemOwner_LibraryItemFragment,
): number | undefined => {
  let owner: number | undefined = undefined;
  if (x.orgPremiumContentConfig) {
    owner = x.orgPremiumContentConfig.createdBy?.id;
  } else if (x.orgSharpConfig) {
    owner = x.orgSharpConfig.createdBy?.id;
  } else {
    owner = x.createdBy?.id;
  }
  return owner;
};

type HasLibraryItemEditPermissionFunc = (args: {
  libraryItem: HasLibraryItemEditPermission_LibraryItemFragment;
  contentOwnerUserId?: number;
}) => boolean;

export const useHasLibraryItemEditPermission =
  (): HasLibraryItemEditPermissionFunc => {
    const userContextState = useUser();
    return useCallback(
      (args) => {
        return !!(
          userContextState?.user?.userType === UserType.Admin ||
          (!!args.libraryItem.sharingPermissions &&
            userHasEditPermissions(
              args.libraryItem.sharingPermissions,
              userContextState,
              args.contentOwnerUserId,
            ))
        );
      },
      [userContextState],
    );
  };

gql`
  fragment HasLibraryItemEditPermission_LibraryItem on LibraryItem {
    id
    sharingPermissions {
      ...LibraryContentSharingPermission
    }
  }
  fragment LibraryUserSharingPermission on UserSharingPermission {
    id
    user {
      id
    }
    permission
  }
  fragment LibraryContentSharingPermission on ContentSharingPermission {
    id
    allAdmins
    allManagers
    individuals {
      ...LibraryUserSharingPermission
    }
  }
`;

gql`
  fragment LibraryItemOwner_LibraryItem on LibraryItem {
    id
    createdBy {
      id
      name
    }
    orgPremiumContentConfig {
      id
      createdBy {
        id
        name
      }
    }
    orgSharpConfig {
      id
      createdBy {
        id
        name
      }
    }
  }
  fragment LibraryItemUrl on LibraryItem {
    id
    publishState
    module {
      id
      publishedVersion {
        id
      }
      draftVersion {
        id
      }
    }
    orgPremiumContentConfig {
      id
      premiumContentConfig {
        id
        catalogModule {
          id
          module {
            id
          }
        }
      }
    }
    course {
      id
    }
    trainingResource {
      id
    }
    orgSharpConfig {
      id
    }
    skill {
      id
    }
  }

  fragment LibraryItemTitle on LibraryItem {
    id
    name {
      en
    }
    type
    orgSharpConfig {
      id
    }
    orgPremiumContentConfig {
      premiumContentConfig {
        catalogModule {
          module {
            libraryItem {
              id
              name {
                en
              }
            }
          }
        }
      }
    }
  }

  fragment LibraryItemAvatar on LibraryItem {
    id
    type
    coverImage {
      ...CoverImage
    }
    trainingResource {
      id
      publishedVersion {
        ...TrainingResourceVersion
      }
      draftVersion {
        ...TrainingResourceVersion
      }
    }
    orgSharpConfig {
      id
    }
    orgPremiumContentConfig {
      id
      premiumContentConfig {
        id
        catalogModule {
          id
          module {
            id
            libraryItem {
              id
              coverImage {
                ...CoverImage
              }
            }
          }
        }
      }
    }
  }

  fragment LibraryItemOwner on LibraryItem {
    createdBy {
      id
      name
    }
  }
`;
gql`
  fragment TrainingResourceVersion__Avatar on TrainingResourceVersion {
    id
    type
    media {
      id
      thumbnailImageUrl
      mediaUrls {
        uuid
        en
      }
      imageUrls {
        id
        original
        thumb
      }
    }
  }
  fragment TrainingResourceAvatar_LibraryItem on LibraryItem {
    id
    trainingResource {
      id
      draftVersion {
        ...TrainingResourceVersion__Avatar
      }
      publishedVersion {
        ...TrainingResourceVersion__Avatar
      }
    }
  }
`;

export const hasCoverImage = (
  coverImage: CoverImageFragment | null | undefined,
): boolean => {
  if (!coverImage) return false;
  return (
    !!coverImage.background || !!coverImage.emojiIcon || !!coverImage.imageUrls
  );
};
